import { Component } from 'react';

// The app had no error boundary anywhere -- any uncaught render error,
// on any page, unmounted the entire React tree to a blank screen with
// no message and nothing in the Supabase logs to explain it (a data
// fetch can fail loudly there; a render-time exception in the browser
// never touches the network at all). This at least shows what broke
// and lets the user get back to a working page instead of a dead one.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Uncaught render error:', error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || null });
  }

  render() {
    if (this.state.error) {
      // Full message + stack (bundle file:line:col -- the prod build now
      // ships source maps, see vite.config.js) so a screenshot of this
      // screen is enough to trace the crash back to real source, no
      // devtools access needed.
      const details = [
        this.state.error?.message || String(this.state.error),
        this.state.error?.stack || '',
        this.state.componentStack || '',
      ].filter(Boolean).join('\n\n');
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-lg space-y-4 text-center">
            <h1 className="font-space font-bold text-xl text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground text-sm">
              This page hit an unexpected error. Reloading usually fixes it -- if it keeps happening, screenshot the message below and send it over.
            </p>
            <pre className="text-left text-[10px] leading-relaxed text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 overflow-auto max-h-80 whitespace-pre-wrap break-all">
              {details}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
